/**
 * Webhook Cart Detector
 * Detects when webhooks have updated the cart to avoid unnecessary rebuilds
 */

class WebhookCartDetector {
  constructor() {
    this.lastCartUpdateCheck = 0;
    this.checkInterval = 5000; // Check every 5 seconds
    this.lastKnownBackendHash = null;
    this.lastKnownCartHash = null;
    this.wasCheckoutActive = false; // Track checkout state changes
    this.isUpdating = false; // Prevent endless loops
    this.lastUpdateTriggered = 0;
    this.updateCooldown = 10000; // 10 second cooldown between updates
    this.lastBackendUpdate = 0; // Track when backend was last updated
    this.storedDraftOrderId = null; // Store draft order ID from backend hash response
    
    // this.initializeDetection();
  }

  initializeDetection() {
    // Start periodic cart change detection
    this.startCartChangeDetection();
    
    // Listen for visibility changes to check when user returns to tab
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.checkForCartChanges();
      }
    });

    // Listen for storage events (in case cart is updated in another tab)
    window.addEventListener('storage', (e) => {
      if (e.key === 'last_webhook_cart_update') {
        this.handleWebhookUpdate();
      }
    });
  }

  /**
   * Start periodic detection of cart changes
   */
  startCartChangeDetection() {
    // Store the interval ID, not overwrite checkInterval value
    this.intervalId = setInterval(() => {
      this.checkForCartChanges();
    }, this.checkInterval);
  }

  /**
   * Check if cart has been updated (potentially by webhook)
   */
  async checkForCartChanges() {
    try {
      // Throttle checks to avoid too frequent requests
      const now = Date.now();
      if (now - this.lastCartUpdateCheck < 3000) { // Min 3 seconds between checks
        return;
      }
      this.lastCartUpdateCheck = now;
      
      // Check if checkout is active - if so, skip detection as checkout component handles updates
      const isCheckoutActive = window.checkoutCartManager && window.checkoutCartManager.isActive;
      const checkoutId = window.checkoutCartManager && window.checkoutCartManager.checkoutId;
      
      // If checkout state changed, reset hash to detect changes properly
      if (this.wasCheckoutActive && !isCheckoutActive) {
        console.log('🔔 Webhook detector: Checkout ended, resetting hash for fresh detection');
        this.lastKnownBackendHash = null;
        this.lastKnownCartHash = null;
        this.storedDraftOrderId = null; // Reset stored draft order ID
      }
      this.wasCheckoutActive = isCheckoutActive;
      
      if (checkoutId) {
        console.log('🔔 Webhook detector: Skipping detection during active checkout (handled by checkout component)');
        return;
      }
      
      // Skip if already updating to prevent endless loops
      if (this.isUpdating) {
        console.log('🔔 Webhook detector: Update in progress, skipping check');
        return;
      }
      
      // Get customer data
      const customerData = this.getCustomerData();
      if (!customerData) {
        console.log('🔔 Webhook detector: Missing user information, skipping check');
        return;
      }
      
      console.log('🔔 Webhook detector: Checking for cart changes...');
      
      // Fetch both frontend cart hash and backend hash in parallel
      const [frontendHashResponse, backendHashResponse] = await Promise.all([
        fetch('/cart?view=hash', { headers: window.getCartHeaders() }),
        fetch(`/apps/stroetmann/api/v1/checkouts/shared/${customerData.userNumber}/hash`, {
          headers: window.getCartHeaders() 
        })
      ]);
      
      // Get current cart hash from frontend endpoint
      let currentCartHash = null;
      if (frontendHashResponse.ok) {
        const hashData = await frontendHashResponse.json();
        currentCartHash = hashData.hash;
        console.log('🔔 Webhook detector: Frontend hash from /cart?view=hash:', currentCartHash);
      } else {
        console.log('🔔 Webhook detector: Failed to fetch frontend cart hash');
        return;
      }
      
      // Get backend hash and store draft order ID
      let backendHash = null;
      let backendGeneratedAt = null;
      let draftOrderId = null;
      
      if (backendHashResponse.ok) {
        const hashData = await backendHashResponse.json();
        backendHash = hashData.line_items_hash;
        backendGeneratedAt = new Date(hashData.generated_at).getTime();
        draftOrderId = hashData.draft_order_id; // Store the draft order ID
        
        // Update stored draft order ID if we have one
        if (draftOrderId) {
          this.storedDraftOrderId = draftOrderId;
          console.log('🔔 Webhook detector: Stored draft order ID:', draftOrderId);
        }
        
        console.log('🔔 Webhook detector: Backend hash data:', {
          draftOrderId: draftOrderId,
          hash: backendHash,
          generatedAt: hashData.generated_at,
          lineItemsCount: hashData.line_items_count
        });
      } else if (backendHashResponse.status === 404) {
        console.log('🔔 Webhook detector: No backend draft order found - using null hash');
        backendHash = null;
        backendGeneratedAt = now;
        this.storedDraftOrderId = null; // Clear stored draft order ID
      } else {
        console.log('🔔 Webhook detector: Failed to fetch backend hash');
        return;
      }
      
      console.log('🔔 Cart comparison:');
      console.log(`   Current cart hash (frontend): ${currentCartHash}`);
      console.log(`   Backend hash: ${backendHash}`);
      console.log(`   Last known backend hash: ${this.lastKnownBackendHash}`);
      console.log(`   Last known cart hash: ${this.lastKnownCartHash}`);
      console.log(`   Stored draft order ID: ${this.storedDraftOrderId}`);
      
      // Check if backend was updated since last check
      const backendWasUpdated = backendGeneratedAt && backendGeneratedAt > this.lastBackendUpdate;
      
      if (backendWasUpdated) {
        console.log('🔔 Webhook detector: Backend was updated since last check');
        this.lastBackendUpdate = backendGeneratedAt;
      }
      
      // Determine if we need to update
      let shouldUpdate = false;
      let updateReason = '';
      
      // Case 1: Backend hash changed and is different from current cart
      if (backendHash !== this.lastKnownBackendHash && backendHash !== currentCartHash) {
        shouldUpdate = true;
        updateReason = 'Backend hash changed and differs from current cart';
      }
      // Case 2: Backend was updated and current cart hash changed from last known
      else if (backendWasUpdated && currentCartHash !== this.lastKnownCartHash) {
        shouldUpdate = true;
        updateReason = 'Backend updated and cart hash changed';
      }
      // Case 3: First time checking (no previous hashes)
      else if (this.lastKnownBackendHash === null && this.lastKnownCartHash === null) {
        if (backendHash !== currentCartHash) {
          shouldUpdate = true;
          updateReason = 'Initial check - hashes differ';
        }
      }
      
      if (shouldUpdate) {
        const now = Date.now();
        
        // Check if we're in cooldown period to prevent endless loops
        if (this.isUpdating) {
          console.log('🔔 Webhook detector: Update already in progress, skipping');
          return;
        }
        
        if (now - this.lastUpdateTriggered < this.updateCooldown) {
          console.log(`🔔 Webhook detector: In cooldown period (${Math.round((this.updateCooldown - (now - this.lastUpdateTriggered)) / 1000)}s remaining)`);
          return;
        }
        
        console.log(`🔔 Webhook detector: ${updateReason} - triggering refresh`);
        this.isUpdating = true;
        this.lastUpdateTriggered = now;
        
        // Update our known hashes
        this.lastKnownBackendHash = backendHash;
        this.lastKnownCartHash = currentCartHash;
        
        // Trigger the update and reset the flag after a delay
        this.handleWebhookUpdate();
        
        setTimeout(() => {
          this.isUpdating = false;
        }, 5000); // Reset after 5 seconds
      } else {
        // Update hashes even if no update needed (for tracking)
        this.lastKnownBackendHash = backendHash;
        this.lastKnownCartHash = currentCartHash;
      }
      
    } catch (error) {
      console.error('❌ Webhook detector: Error checking for cart changes:', error);
    }
  }

  /**
   * Handle webhook update by fetching latest cart data and updating the page
   */
  async handleWebhookUpdate() {
    try {
      console.log('🔔 Webhook detector: Handling webhook update...');
      
      // Get customer data
      const customerData = this.getCustomerData();
      if (!customerData) {
        console.error('❌ Webhook detector: Missing customer data for webhook update');
        return;
      }
      
      // Fetch backend cart items using stored draft order ID
      const cartItems = await this.fetchBackendCartItems(customerData);
      
      if (!cartItems) {
        console.error('❌ Webhook detector: Failed to fetch backend cart items');
        return;
      }
      
      console.log('🔔 Webhook detector: Fetched backend cart items:', cartItems);
      
      // Update the customer's cart with the backend data
      await this.updateCustomerCart(cartItems);
      
      // Update localStorage to trigger other components
      localStorage.setItem('last_webhook_cart_update', Date.now().toString());
      
      // Dispatch custom event for other components to listen to
      document.dispatchEvent(new CustomEvent('cart:draft-order:updated', {
        detail: {
          source: 'webhook-detector',
          timestamp: Date.now(),
          cartItems: cartItems,
          draftOrderId: this.storedDraftOrderId
        }
      }));
      
      console.log('✅ Webhook detector: Cart update completed successfully');
      
    } catch (error) {
      console.error('❌ Webhook detector: Error handling webhook update:', error);
      
      // Dispatch error event
      document.dispatchEvent(new CustomEvent('cart:draft-order:updated', {
        detail: {
          source: 'webhook-detector',
          timestamp: Date.now(),
          detected: true,
          error: error.message
        }
      }));
    }
  }

  /**
   * Update the customer's cart with backend line items
   */
  async updateCustomerCart(backendLineItems) {
    try {
      console.log('🛒 Webhook detector: Updating customer cart with backend data...');
      
      // Get current cart state
      const currentCart = await this.getCurrentCart();
      
      // Calculate changes between current cart and backend data
      const changes = this.calculateCartChanges(currentCart, backendLineItems);
      
      if (changes.hasChanges) {
        console.log('🔄 Webhook detector: Applying cart changes:', changes);
        
        // Prevent customer interactions during update
        this.setUpdateMode(true);
        
        try {
          // Apply changes to cart
          await this.applyCartChanges(changes);
          
          // Update frontend using AJAX cart API
          await this.updateFrontendSections();
          
          console.log('✅ Webhook detector: Smart cart update completed successfully');
        } finally {
          // Re-enable customer interactions
          this.setUpdateMode(false);
        }
      } else {
        console.log('ℹ️ Webhook detector: No changes detected, skipping cart update');
      }
      
    } catch (error) {
      console.error('❌ Webhook detector: Error updating customer cart:', error);
      throw error;
    }
  }

  /**
   * Get current cart state
   */
  async getCurrentCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error(`Failed to get current cart: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('❌ Webhook detector: Error getting current cart:', error);
      throw error;
    }
  }

  /**
   * Calculate changes between current cart and backend data
   */
  calculateCartChanges(currentCart, backendLineItems) {
    const changes = {
      itemsToAdd: [],
      itemsToUpdate: [],
      itemsToRemove: [],
      hasChanges: false
    };

    console.log('🔍 Webhook detector: Comparing cart data:');
    console.log('   Current cart items:', currentCart.items);
    console.log('   Backend line items:', backendLineItems);

    // Create maps for efficient comparison
    const currentItemsMap = new Map();
    currentCart.items.forEach(item => {
      const key = this.getItemKey(item, 'frontend');
      currentItemsMap.set(key, item);
      console.log(`   Frontend item key: ${key}`, item);
    });

    const backendItemsMap = new Map();
    backendLineItems.forEach(item => {
      const key = this.getItemKey(item, 'backend');
      backendItemsMap.set(key, item);
      console.log(`   Backend item key: ${key}`, item);
    });

    // Find items to add (in backend but not in current cart)
    for (const [key, backendItem] of backendItemsMap) {
      if (!currentItemsMap.has(key)) {
        changes.itemsToAdd.push(backendItem);
        changes.hasChanges = true;
        console.log(`   ➕ Adding item: ${key}`);
      }
    }

    // Find items to remove (in current cart but not in backend)
    for (const [key, currentItem] of currentItemsMap) {
      if (!backendItemsMap.has(key)) {
        changes.itemsToRemove.push(currentItem);
        changes.hasChanges = true;
        console.log(`   ➖ Removing item: ${key}`);
      }
    }

    // Find items to update (in both but with different quantities or properties)
    for (const [key, backendItem] of backendItemsMap) {
      const currentItem = currentItemsMap.get(key);
      if (currentItem && this.itemsAreDifferent(currentItem, backendItem)) {
        changes.itemsToUpdate.push({
          current: currentItem,
          backend: backendItem
        });
        changes.hasChanges = true;
        console.log(`   🔄 Updating item: ${key}`);
      }
    }

    console.log('🔍 Webhook detector: Changes detected:', changes);
    return changes;
  }

  /**
   * Generate a unique key for cart item comparison
   */
  getItemKey(item, source) {
    // Handle different data structures between frontend and backend
    let variantId;
    
    if (source === 'frontend') {
      // Frontend cart uses variant_id
      variantId = item.variant_id || item.id;
    } else {
      // Backend uses id (which is the variant ID)
      variantId = item.id;
    }
    
    // Handle properties differently for frontend vs backend
    let properties = {};
    
    if (source === 'frontend') {
      // Frontend properties are already in object format
      properties = item.properties || {};
    } else {
      // Backend properties are in array format, convert to object
      if (item.properties && Array.isArray(item.properties)) {
        item.properties.forEach(prop => {
          if (prop.key && prop.value !== undefined) {
            properties[prop.key] = prop.value;
          }
        });
      }
    }
    
    // Include properties in the key for items with custom properties
    if (Object.keys(properties).length > 0) {
      const propertiesString = Object.entries(properties)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, value]) => `${key}:${value}`)
        .join('|');
      return `${variantId}:${propertiesString}`;
    }
    
    return variantId;
  }

  /**
   * Compare two items to see if they're different
   */
  itemsAreDifferent(currentItem, backendItem) {
    // Check quantity
    if (currentItem.quantity !== backendItem.quantity) {
      return true;
    }

    // Check properties
    const currentProps = currentItem.properties || {};
    const backendProps = backendItem.properties || [];

    // Convert backend properties array to object for comparison
    const backendPropsObj = {};
    backendProps.forEach(prop => {
      backendPropsObj[prop.key] = prop.value;
    });

    // Compare property keys
    const currentKeys = Object.keys(currentProps).sort();
    const backendKeys = Object.keys(backendPropsObj).sort();

    if (currentKeys.length !== backendKeys.length) {
      return true;
    }

    // Compare property values
    for (const key of currentKeys) {
      if (currentProps[key] !== backendPropsObj[key]) {
        return true;
      }
    }

    return false;
  }

  /**
   * Apply calculated changes to the cart
   */
  async applyCartChanges(changes) {
    try {
      // Remove items that are no longer in backend
      for (const item of changes.itemsToRemove) {
        await this.removeItemFromCart(item.key);
      }

      // Update existing items
      for (const { current, backend } of changes.itemsToUpdate) {
        await this.updateItemInCart(current.key, backend);
      }

      // Add new items
      for (const item of changes.itemsToAdd) {
        await this.addLineItemToCart(item);
      }

    } catch (error) {
      console.error('❌ Webhook detector: Error applying cart changes:', error);
      throw error;
    }
  }

  /**
   * Remove item from cart
   */
  async removeItemFromCart(itemKey) {
    try {
      console.log('🗑️ Webhook detector: Removing item from cart:', itemKey);
      
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: itemKey,
          quantity: 0
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to remove item from cart: ${response.status}`);
      }

      console.log('✅ Webhook detector: Item removed from cart successfully');
      
    } catch (error) {
      console.error('❌ Webhook detector: Error removing item from cart:', error);
      throw error;
    }
  }

  /**
   * Update item in cart
   */
  async updateItemInCart(itemKey, backendItem) {
    try {
      console.log('🔄 Webhook detector: Updating item in cart:', itemKey, backendItem);
      
      // Prepare the update data
      const updateData = {
        id: itemKey,
        quantity: backendItem.quantity
      };

      // Add properties if they exist
      if (backendItem.properties && backendItem.properties.length > 0) {
        updateData.properties = {};
        for (const prop of backendItem.properties) {
          updateData.properties[prop.key] = prop.value;
        }
      }

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        throw new Error(`Failed to update item in cart: ${response.status}`);
      }

      console.log('✅ Webhook detector: Item updated in cart successfully');
      
    } catch (error) {
      console.error('❌ Webhook detector: Error updating item in cart:', error);
      throw error;
    }
  }

  /**
   * Update frontend sections using AJAX cart API
   */
  async updateFrontendSections() {
    try {
      console.log('🎨 Webhook detector: Updating frontend sections...');
      
      // Get current cart state via AJAX
      const cartResponse = await fetch('/cart.js', {
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!cartResponse.ok) {
        throw new Error(`Failed to fetch cart data: ${cartResponse.status}`);
      }
      
      const cartData = await cartResponse.json();
      console.log('🎨 Webhook detector: Fetched current cart data:', cartData);
      
      // Update cart elements with the new data
      await this.updateCartElements(cartData);
      
      console.log('✅ Webhook detector: Frontend sections updated successfully');
      
    } catch (error) {
      console.error('❌ Webhook detector: Error updating frontend sections:', error);
      throw error;
    }
  }

  /**
   * Update cart elements with new cart data
   */
  async updateCartElements(cartData) {
    try {
      console.log('🎨 Webhook detector: Updating cart elements...');
      
      // Update cart count/badge
      this.updateCartCount(cartData.item_count);
      
      // Update cart total
      this.updateCartTotal(cartData.total_price);
      
      // Update cart items list
      this.updateCartItems(cartData.items);
      
      // Update cart drawer (if exists)
      this.updateCartDrawer(cartData);
      
      // Trigger custom event for other components
      this.triggerCartUpdateEvent(cartData);
      
    } catch (error) {
      console.error('❌ Webhook detector: Error updating cart elements:', error);
      throw error;
    }
  }

  /**
   * Update cart count/badge
   */
  updateCartCount(itemCount) {
    const countSelectors = [
      '[data-cart-count]',
      '.cart-count',
      '#cart-count',
      '.cart-badge',
      '[class*="cart-count"]',
      '[class*="cart-badge"]'
    ];
    
    countSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        element.textContent = itemCount;
        console.log(`✅ Webhook detector: Updated cart count element: ${selector}`);
      });
    });
  }

  /**
   * Update cart total
   */
  updateCartTotal(totalPrice) {
    const totalSelectors = [
      '[data-cart-total]',
      '.cart-total',
      '#cart-total',
      '[class*="cart-total"]'
    ];
    
    const formattedPrice = this.formatMoney(totalPrice);
    
    totalSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        element.textContent = formattedPrice;
        console.log(`✅ Webhook detector: Updated cart total element: ${selector}`);
      });
    });
  }

  /**
   * Update cart items list using Shopify's section rendering API
   */
  updateCartItems(items) {
    console.log('🎨 Webhook detector: Updating cart items using section rendering...');
    
    // Use Shopify's section rendering API to get fresh cart HTML
    fetch('/cart?view=ajax')
      .then(response => response.text())
      .then(html => {
        console.log('🎨 Webhook detector: Received fresh cart HTML from server');
        console.log('🎨 Webhook detector: HTML length:', html.length);
        console.log('🎨 Webhook detector: HTML preview:', html.substring(0, 500));
        this.updateCartWithServerHTML(html);
      })
      .catch(error => {
        console.error('❌ Webhook detector: Error fetching cart HTML:', error);
        this.triggerCartRefresh();
      });
  }

  /**
   * Update cart with HTML received from server
   */
  updateCartWithServerHTML(html) {
    try {
      // Create a temporary div to parse the HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      
      console.log('🎨 Webhook detector: Parsing server HTML for cart elements...');
      
      // Find the cart-items custom element in the server HTML
      const serverCartItems = tempDiv.querySelector('cart-items');
      
      if (serverCartItems) {
        console.log('🎨 Webhook detector: Found cart-items element in server HTML');
        
        // Find the cart-items element on the current page
        const pageCartItems = document.querySelector('cart-items');
        
        if (pageCartItems) {
          console.log('🎨 Webhook detector: Found cart-items element on page, updating...');
          
          // Update the entire cart-items element content
          pageCartItems.innerHTML = serverCartItems.innerHTML;
          
          console.log('✅ Webhook detector: Successfully updated cart-items element');
        } else {
          console.log('⚠️ Webhook detector: No cart-items element found on page, trying alternative selectors...');
          
          // Fallback: try to find cart items table directly
          const serverCartTable = tempDiv.querySelector('.cart-items');
          const pageCartTable = document.querySelector('.cart-items');
          
          if (serverCartTable && pageCartTable) {
            console.log('🎨 Webhook detector: Found cart-items table, updating...');
            pageCartTable.innerHTML = serverCartTable.innerHTML;
            console.log('✅ Webhook detector: Successfully updated cart-items table');
          } else {
            console.log('⚠️ Webhook detector: No cart table found, triggering refresh');
            this.triggerCartRefresh();
          }
        }
      } else {
        console.log('⚠️ Webhook detector: No cart-items element found in server HTML, trying alternative selectors...');
        
        // Try alternative selectors
        const alternativeSelectors = [
          '.cart-items',
          '[data-cart-items]',
          '#cart-items',
          '[class*="cart-items"]'
        ];
        
        let found = false;
        alternativeSelectors.forEach(selector => {
          const serverElement = tempDiv.querySelector(selector);
          const pageElement = document.querySelector(selector);
          
          if (serverElement && pageElement) {
            console.log(`🎨 Webhook detector: Found element with selector: ${selector}`);
            pageElement.innerHTML = serverElement.innerHTML;
            console.log(`✅ Webhook detector: Successfully updated element: ${selector}`);
            found = true;
          }
        });
        
        if (!found) {
          console.log('⚠️ Webhook detector: No cart elements found, triggering refresh');
          this.triggerCartRefresh();
        }
      }
      
    } catch (error) {
      console.error('❌ Webhook detector: Error updating cart with server HTML:', error);
      this.triggerCartRefresh();
    }
  }

  /**
   * Trigger a cart refresh using theme's built-in methods
   */
  triggerCartRefresh() {
    try {
      // Try to use theme's cart refresh method if available
      if (window.theme && window.theme.cart && window.theme.cart.refresh) {
        console.log('🎨 Webhook detector: Using theme cart refresh method');
        window.theme.cart.refresh();
        return;
      }
      
      // Try to trigger a cart update event
      if (window.Shopify && window.Shopify.onCartUpdate) {
        console.log('🎨 Webhook detector: Using Shopify cart update');
        window.Shopify.onCartUpdate();
        return;
      }
      
      // Fallback: reload the cart page
      console.log('🎨 Webhook detector: Triggering page reload for cart update');
      if (window.location.pathname.includes('/cart')) {
        window.location.reload();
      }
      
    } catch (error) {
      console.error('❌ Webhook detector: Error triggering cart refresh:', error);
    }
  }



  /**
   * Update cart drawer
   */
  updateCartDrawer(cartData) {
    const drawerSelectors = [
      '[data-cart-drawer]',
      '.cart-drawer',
      '#cart-drawer',
      '[class*="cart-drawer"]'
    ];
    
    drawerSelectors.forEach(selector => {
      const drawer = document.querySelector(selector);
      if (drawer) {
        // Update drawer content with cart data
        this.updateDrawerContent(drawer, cartData);
        console.log(`✅ Webhook detector: Updated cart drawer: ${selector}`);
      }
    });
  }

  /**
   * Update drawer content
   */
  updateDrawerContent(drawer, cartData) {
    try {
      console.log('🎨 Webhook detector: Updating drawer content...');
      
      // Update cart count in drawer
      const countElement = drawer.querySelector('#cart-drawer__count, [data-cart-count], .cart-count');
      if (countElement) {
        countElement.textContent = `(${cartData.item_count})`;
      }
      
      // For drawer cart items, use the same section rendering approach
      // The drawer uses the same cart-items snippet, so we can reuse the same logic
      console.log('🎨 Webhook detector: Updating drawer cart items...');
      this.updateCartItems(cartData.items);
      
    } catch (error) {
      console.error('❌ Webhook detector: Error updating drawer content:', error);
    }
  }

  /**
   * Format money value
   */
  formatMoney(cents) {
    try {
      // Use Shopify's money format if available
      if (window.Shopify && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(cents);
      }
      
      // Fallback formatting
      const dollars = (cents / 100).toFixed(2);
      return `$${dollars}`;
    } catch (error) {
      console.error('❌ Webhook detector: Error formatting money:', error);
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  /**
   * Trigger custom event for other components
   */
  triggerCartUpdateEvent(cartData) {
    const event = new CustomEvent('cart:updated', {
      detail: {
        cart: cartData,
        timestamp: Date.now(),
        source: 'webhook-detector'
      }
    });
    
    document.dispatchEvent(event);
    console.log('✅ Webhook detector: Triggered cart:updated event');
  }

  /**
   * Set update mode to prevent customer interactions
   */
  setUpdateMode(isUpdating) {
    if (isUpdating) {
      // Add overlay to prevent interactions
      this.addUpdateOverlay();
      
      // Disable form submissions and buttons
      this.disableInteractions();
      
      console.log('🔒 Webhook detector: Update mode enabled - preventing customer interactions');
    } else {
      // Remove overlay
      this.removeUpdateOverlay();
      
      // Re-enable interactions
      this.enableInteractions();
      
      console.log('🔓 Webhook detector: Update mode disabled - customer interactions restored');
    }
  }

  /**
   * Add overlay to prevent interactions during update
   */
  addUpdateOverlay() {
    if (this.updateOverlay) {
      return; // Already exists
    }

    this.updateOverlay = document.createElement('div');
    this.updateOverlay.id = 'webhook-update-overlay';
    this.updateOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.3);
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 16px;
    `;
    this.updateOverlay.innerHTML = `
      <div style="background: #333; padding: 20px; border-radius: 8px; text-align: center;">
        <div style="margin-bottom: 10px;">🔄</div>
        <div>Updating cart...</div>
      </div>
    `;
    
    document.body.appendChild(this.updateOverlay);
  }

  /**
   * Remove update overlay
   */
  removeUpdateOverlay() {
    if (this.updateOverlay) {
      this.updateOverlay.remove();
      this.updateOverlay = null;
    }
  }

  /**
   * Disable customer interactions
   */
  disableInteractions() {
    // Disable all buttons and form inputs
    const interactiveElements = document.querySelectorAll('button, input, select, textarea, a[href]');
    this.disabledElements = [];
    
    interactiveElements.forEach(element => {
      if (!element.disabled && !element.hasAttribute('data-webhook-update-disabled')) {
        element.setAttribute('data-webhook-update-disabled', 'true');
        element.disabled = true;
        this.disabledElements.push(element);
      }
    });
  }

  /**
   * Re-enable customer interactions
   */
  enableInteractions() {
    // Re-enable previously disabled elements
    if (this.disabledElements) {
      this.disabledElements.forEach(element => {
        element.disabled = false;
        element.removeAttribute('data-webhook-update-disabled');
      });
      this.disabledElements = [];
    }
  }

  /**
   * Clear the current cart
   */
  async clearCurrentCart() {
    try {
      console.log('🛒 Webhook detector: Clearing current cart...');
      
      const response = await fetch('/cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to clear cart: ${response.status}`);
      }
      
      console.log('✅ Webhook detector: Cart cleared successfully');
      
    } catch (error) {
      console.error('❌ Webhook detector: Error clearing cart:', error);
      throw error;
    }
  }

  /**
   * Add a line item to the cart
   */
  async addLineItemToCart(lineItem) {
    try {
      console.log('🛒 Webhook detector: Adding line item to cart:', lineItem);
      
      // Prepare the cart item data
      const cartItem = {
        id: lineItem.id,
        quantity: lineItem.quantity
      };
      
      // Add properties if they exist
      if (lineItem.properties && lineItem.properties.length > 0) {
        cartItem.properties = {};
        for (const prop of lineItem.properties) {
          cartItem.properties[prop.key] = prop.value;
        }
      }
      
      // Add item to cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(cartItem)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Failed to add item to cart: ${response.status} - ${JSON.stringify(errorData)}`);
      }
      
      const addedItem = await response.json();
      console.log('✅ Webhook detector: Added item to cart:', addedItem);
      
    } catch (error) {
      console.error('❌ Webhook detector: Error adding line item to cart:', error);
      throw error;
    }
  }

  /**
   * Get customer data for API calls
   */
  getCustomerData() {
    if (!window.customer || !window.customer.id) {
      return null;
    }
    
    return {
      customerId: window.customer.id,
      userName: window.customer.userName,
      userNumber: window.customer.currentUserNumber
    };
  }

  /**
   * Fetch backend cart items using the stored draft order ID
   */
  async fetchBackendCartItems(customerData) {
    try {
      let draftOrderId = this.storedDraftOrderId;
      
      // If we don't have a stored draft order ID, try to get it from the hash endpoint
      if (!draftOrderId) {
        console.log('🔔 Webhook detector: No stored draft order ID, fetching from hash endpoint...');
        
        const hashUrl = `/apps/stroetmann/api/v1/checkouts/shared/${customerData.userNumber}/hash`;
        const hashResponse = await fetch(hashUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });
        
        if (!hashResponse.ok) {
          throw new Error(`Hash request failed: ${hashResponse.status}`);
        }
        
        const hashData = await hashResponse.json();
        draftOrderId = hashData.draft_order_id;
        
        if (!draftOrderId) {
          throw new Error('No draft order ID found in hash response');
        }
        
        // Store the draft order ID for future use
        this.storedDraftOrderId = draftOrderId;
        console.log('🔔 Webhook detector: Retrieved and stored draft order ID:', draftOrderId);
      }
      
      // Now fetch the cart items using the draft order ID
      const itemsUrl = `/apps/stroetmann/api/v1/checkouts/shared/${draftOrderId}/items`;
      const itemsResponse = await fetch(itemsUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      if (!itemsResponse.ok) {
        throw new Error(`Items request failed: ${itemsResponse.status}`);
      }
      
      const itemsData = await itemsResponse.json();
      return itemsData;
      
    } catch (error) {
      console.error('❌ Webhook detector: Error fetching backend cart items:', error);
      return null;
    }
  }

  /**
   * Stop detection (for cleanup)
   */
  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}

// // Initialize webhook detection only if customer is logged in
// if (window.customer && window.customer.id) {
//   window.webhookCartDetector = new WebhookCartDetector();
//   console.log('✅ Webhook Cart Detector initialized');
// } else {
//   console.log('ℹ️ Webhook Cart Detector: Skipped (no customer logged in)');
// }

// // Cleanup on page unload
// window.addEventListener('beforeunload', () => {
//   if (window.webhookCartDetector) {
//     window.webhookCartDetector.stop();
//   }
// });